import { Button, Carousel } from '@acorns/web-components'
import styled, { css } from 'styled-components'

import { OPTIMIZELY_CONTROL_EXPERIMENT, OPTIMIZELY_EXPERIMENT_VARIATION_1 } from 'app-constants'
import { style } from 'theme'

export const Container = styled.div`
  padding: 40px 64px 80px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 1024px) {
    padding: 20px 32px 60px;
  }

  @media (max-width: 767px) {
    padding: 0px;
  }
`

export const Headline = styled.h2`
  font-size: 48px;
  line-height: 52px;
  letter-spacing: -0.6px;
  color: ${style(theme => theme.colors.slate)};
  margin: 0 auto 22px;
  text-align: center;

  @media (max-width: 1024px) {
    font-size: 32px;
    line-height: 48px;
    letter-spacing: normal;
  }

  @media (max-width: 767px) {
    line-height: 38px;
    margin-bottom: 14px;
    letter-spacing: -0.4px;
  }
`

export const Subheadline = styled.h4`
  font-size: 18px;
  line-height: 30px;
  letter-spacing: 0;
  color: ${style(theme => theme.colors.slate)};
  max-width: 549px;
  margin: 0 auto 78px;
  text-align: center;

  @media (max-width: 1024px) {
    margin-bottom: 62px;
  }

  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 25px;
    margin-bottom: 48px;
  }
`

export const SpinnerContainer = styled.div`
  height: calc(50vh - 40px);
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const TiersWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 70px;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`

export const TierCard = styled.div<{ variant: string }>`
  position: relative;
  display: inline-block;
  ${props => props.variant === OPTIMIZELY_CONTROL_EXPERIMENT && 'max-width: 334px;'}
  padding: 40px;
  height: fit-content;
  border-radius: 10px;
  background: ${style(theme => theme.colors.white)};
  box-shadow: ${style(theme => theme.shadows.lgMedium)};
  &:not(:last-child) {
    margin-right: 32px;
  }

  @media (max-width: 1024px) {
    width: ${props =>
      props.variant === OPTIMIZELY_EXPERIMENT_VARIATION_1 ? '100%' : 'calc(50% - 16px)'};

    &:not(:last-child) {
      margin-right: 24px;
    }
  }

  @media (max-width: 767px) {
    width: 100%;
    margin: 0 auto;

    &:not(:last-child) {
      margin-bottom: 40px;
      margin-right: auto;
    }
  }
`

export const TierCardHeader = styled.div<{ variant: string }>`
  padding: ${props => {
    if (props.variant === OPTIMIZELY_CONTROL_EXPERIMENT) {
      return '0'
    } else if (props.variant === OPTIMIZELY_EXPERIMENT_VARIATION_1) {
      return '40px 100px 35px 30px'
    } else {
      return '40px 20px 20px'
    }
  }}
  text-align: ${props => (props.variant === OPTIMIZELY_EXPERIMENT_VARIATION_1 ? 'left' : 'center')};
  z-index: 10;
  max-width: 450px;
  margin: 0 auto 8px;
`

export const TierCardTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 32px;
  line-height: 36px;
  font-weight: 600;
  letter-spacing: -0.4px;
  color: ${style(theme => theme.colors.slate)};

  @media (max-width: 1024px) {
    font-size: 28px;
    letter-spacing: -0.35px;
    margin-bottom: 4px;
  }

  @media (max-width: 767px) {
    line-height: 32px;
    letter-spacing: -0.2px;
    margin-bottom: 8px;
  }
`

export const TierCardPrice = styled.h6`
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  letter-spacing: -0.22px;
  color: ${style(theme => theme.colors.green)};
  padding-bottom: 24px;
  margin: 0;

  @media (max-width: 1024px) {
    font-size: 14px;
    letter-spacing: -0.19px;
  }

  @media (max-width: 767px) {
    line-height: 18px;
    letter-spacing: -0.18px;
    padding-bottom: 16px;
  }
`

export const TierCardBody = styled.p`
  font-size: 16px;
  line-height: 24px;
  margin: 0 auto 24px;
  color: ${style(theme => theme.colors.slate)};

  @media (max-width: 1024px) {
    font-size: 14px;
    line-height: 22px;
  }

  @media (max-width: 767px) {
    line-height: 24px;
  }
`

export const TierCardCta = styled(Button)`
  width: 100%;
  margin: 0 auto;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 600;
  transition: background 300ms;
  margin-top: 52px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    max-width: calc(100% - 40px);
    padding: 0 20px;
    position: sticky;
    bottom: 20px;
  }
`

export const ProductHeaderWrapper = styled.div<{ hasValueProps?: boolean }>`
  padding: 32px 0;
  display: flex;
  align-items: center;

  ${props =>
    props.hasValueProps &&
    css`
      border-bottom: none;
      position: relative;
      top: -1px;
      background: ${style(theme => theme.colors.white)};
      border-top: 1px solid rgba(222, 222, 222, 0.3);
    `}

  @media (max-width: 1024px) {
    padding: 24px 16px;
  }

  @media (max-width: 767px) {
    padding: 28px 24px;
  }
`

export const ProductHeaderCopy = styled.div`
  display: inline-block;
  margin-left: 15px;
  min-width: 150px;
`

export const ProductHeaderTitle = styled.p<{ isInvalid?: boolean }>`
  font-size: 16px;
  line-height: 23px;
  font-weight: 600;
  margin: 0;
  color: ${props => style(theme => (props.isInvalid ? theme.colors.stone : theme.colors.slate))};

  @media (max-width: 1024px) {
    font-size: 14px;
    line-height: 18px;
  }
`

export const ProductHeaderSubtitle = styled.p`
  font-size: 12px;
  line-height: 16px;
  margin: 0;
  color: ${style(theme => theme.colors.stone)};
`

export const ValuePropsWrapper = styled.ul`
  padding: 0 24px 40px;
  margin: 0;

  @media (max-width: 1024px) {
    padding: 0 16px 32px 9px;
  }

  @media (max-width: 767px) {
    padding: 0 24px 40px 22px;
  }
`

export const ValueProp = styled.li`
  font-size: 14px;
  line-height: 22px;
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
`

export const ValuePropBullet = styled.div`
  width: 36px;
  margin-right: 12px;
  text-align: center;
  color: ${style(theme => theme.colors.stone)};

  @media (max-width: 767px) {
    width: 34px;
    margin: 0 10px 0 -2px;
  }
`

export const ValuePropCopy = styled.div`
  width: 100%;
  max-width: calc(100% - 48px);
  font-size: 14px;
  line-height: 22px;
  display: inline-block;
  color: ${style(theme => theme.colors.slate)};

  @media (max-width: 767px) {
    max-width: calc(100% - 46px);
  }
`

export const PreferredTierBadge = styled.span`
  width: 132px;
  height: 32px;
  line-height: 32px;
  position: absolute;
  top: -16px;
  left: calc(50% - 66px);
  display: block;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.24px;
  background: #4da1ef;
  color: ${style(theme => theme.colors.white)};
  border-radius: 100px;
  text-align: center;

  @media (max-width: 767px) {
    top: -15px;
    height: 30px;
    line-height: 30px;
  }
`

export const StyledCarousel = styled(Carousel)`
  box-shadow: none;

  & .slick-active button::before {
    background-color: ${({ theme }) => theme.colors.green} !important;
  }
`

export const CarouselTierItemContainer = styled.div`
  padding: 40px 0px 0px 0px;
  width: 100%;

  @media (max-width: 1024px) {
    padding: 20px 0px 0px 0px;
  }

  @media (max-width: 767px) {
    padding: 24px 0px 0px 0px;
  }
`

export const TierControlItem = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 52px;
  }
`

export const IconContainer = styled.div`
  border-radius: 50%;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  background-color: ${({ theme }) => theme.colors.ivory};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  & path {
    fill: ${({ theme }) => theme.colors.stoneLight};
  }
`

export const TierControlItemV1 = styled.div`
  width: 100%;
  margin: 0px auto;
  padding: 30px 0px;
  border-bottom: 1px solid ${style(theme => theme.colors.ivory)};
  max-width: 400px;

  &:last-child {
    margin-bottom: 22px;
    border-bottom: none;
  }
`

export const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`

export const GroupIcon = styled.img`
  width: 25px;
  height: 25px;
  margin-right: 12px;
`

export const GroupTitle = styled.p<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  color: ${props =>
    props.disabled ? style(theme => theme.colors.stone) : style(theme => theme.colors.slate)};
  font-weight: 600;
  font-size: 18px;
  line-height: 26px;
  & svg {
    margin-right: 12px;
  }

  & path {
    fill: ${props =>
      props.disabled ? style(theme => theme.colors.stone) : style(theme => theme.colors.slate)};
  }
`

export const GroupBody = styled.div`
  margin-top: 20px;
`

export const OptionText = styled.p<{ disabled: boolean }>`
  display: flex;
  color: ${style(theme => theme.colors.slate)};
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;

  & span {
    margin-left: 12px;
    width: calc(100% - 40px);
  }

  & path {
    fill: ${props =>
      style(theme => (props.disabled ? theme.colors.stoneLight : theme.colors.green))};
  }
`

export const IconContainerV2 = styled.div`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const TierHeadImage = styled.img`
  position: absolute;
  right: 0px;
  bottom: 0px;
`

export const TierHeadContainer = styled.div`
  width: 100%;
  position: relative;
`

export const TierBodyContainer = styled.div`
  @media (max-width: 767px) {
    padding: 0px 30px;
  }
`

export const Divider = styled.div`
  width: 100%;
  height: 10px;
  background: ${style(theme => theme.colors.ivoryLight)};
`

export const SubContainer = styled.div<{ variant: string }>`
  width: 100%;
  margin: 0 auto;
  background: ${props =>
    props.variant === OPTIMIZELY_EXPERIMENT_VARIATION_1
      ? style(theme => theme.colors.white)
      : 'transparent'};
  border-radius: 10px;
`

export const SwitchContainer = styled.div`
  max-width: 315px;
  margin: 0 auto;
  padding-top: 50px;

  @media (max-width: 767px) {
    padding-top: 0px;
  }
`
